/**
 * Sidebar do painel Admin White Label (/wl)
 * Esconde dinamicamente itens em que o operador não tem can_view.
 */
import { NavLink } from '@/components/NavLink';
import {
  LayoutDashboard,
  Building2,
  Plug,
  Users,
  DollarSign,
  UserCog,
  Shield,
  Activity,
  Settings,
  CreditCard,
  Package,
  LayoutGrid,
  KeyRound,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';

// Menus agrupados por seção lógica
const MENU_GROUPS: Array<{
  label: string;
  items: Array<{ title: string; url: string; icon: typeof LayoutDashboard; menuKey: string }>;
}> = [
  {
    label: 'Visão Geral',
    items: [
      { title: 'Dashboard', url: '/wl', icon: LayoutDashboard, menuKey: 'wl.dashboard' },
    ],
  },
  {
    label: 'Plataforma',
    items: [
      { title: 'Tenants',              url: '/wl/tenants',       icon: Building2, menuKey: 'wl.tenants' },
      { title: 'Instâncias',           url: '/wl/instances',     icon: Plug,      menuKey: 'wl.instances' },
      { title: 'Usuários dos Tenants', url: '/wl/tenant-users',  icon: Users,     menuKey: 'wl.tenant_users' },
    ],
  },
  {
    label: 'Catálogo',
    items: [
      { title: 'Planos',  url: '/wl/plans',  icon: CreditCard, menuKey: 'wl.plans' },
      { title: 'Add-ons', url: '/wl/addons', icon: Package,    menuKey: 'wl.addons' },
      { title: 'Matriz de Planos', url: '/wl/plan-matrix', icon: LayoutGrid, menuKey: 'wl.plan_matrix' },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { title: 'Financeiro', url: '/wl/financial', icon: DollarSign, menuKey: 'wl.financial' },
    ],
  },
  {
    label: 'Configurações',
    items: [
      { title: 'Operadores WL',  url: '/wl/operators', icon: UserCog,  menuKey: 'wl.wl_users' },
      { title: 'Perfis e Permissões', url: '/wl/roles', icon: Shield,   menuKey: 'wl.wl_roles' },
      { title: 'Sessões de Suporte', url: '/wl/sessions', icon: KeyRound, menuKey: 'wl.audit' },
      { title: 'Auditoria',      url: '/wl/audit',     icon: Activity, menuKey: 'wl.audit' },
      { title: 'Configurações',  url: '/wl/settings',  icon: Settings, menuKey: 'wl.settings' },
    ],
  },
];

export function WhiteLabelSidebar() {
  const { state, isMobile } = useSidebar();
  const collapsed = isMobile ? false : state === 'collapsed';
  const { branding } = useSystemSettings();
  const { canView, isObserver, roleName } = useWlPermissions();

  // Observador enxerga tudo (somente leitura via RPCs)
  const isVisible = (key: string) => isObserver || canView(key);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border/50 px-3 py-3">
        <div className="flex items-center gap-3">
          {branding.icon_url ? (
            <img src={branding.icon_url} alt="" className="h-8 w-8 shrink-0 object-contain rounded-lg" />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              {branding.app_name[0]?.toUpperCase() || 'W'}
            </div>
          )}
          {!collapsed && (
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground truncate text-sm">{branding.app_name}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">White Label</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-1">
        {MENU_GROUPS.map((group, idx) => {
          const visibleItems = group.items.filter(i => isVisible(i.menuKey));
          if (visibleItems.length === 0) return null;

          return (
            <SidebarGroup key={group.label} className="py-0">
              {!collapsed && (
                <SidebarGroupLabel className="text-[10px] font-semibold text-sidebar-foreground/50 uppercase tracking-wider px-2 mb-0 py-1">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map(item => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end={item.url === '/wl'}
                          className="flex items-center gap-3 px-3 py-1 rounded-lg transition-all duration-200 ease-in-out text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-foreground hover:translate-x-1"
                          activeClassName="bg-sidebar-primary/20 text-sidebar-primary font-medium border-l-4 border-sidebar-primary -ml-[2px] pl-[14px] hover:translate-x-0"
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="truncate text-sm">{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
              {idx < MENU_GROUPS.length - 1 && !collapsed && (
                <Separator className="mt-1 bg-sidebar-border opacity-50" />
              )}
            </SidebarGroup>
          );
        })}

        {!collapsed && isObserver && (
          <div className="px-3 pt-3">
            <Badge variant="outline" className="w-full justify-center text-[10px]">
              Modo observador (read-only)
            </Badge>
          </div>
        )}
        {!collapsed && roleName && !isObserver && (
          <div className="px-3 pt-3 text-[11px] text-muted-foreground truncate">
            Perfil: <span className="font-medium text-foreground">{roleName}</span>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
